import React, { useMemo } from 'react';

import classnames from 'classnames';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuTypePropType } from 'ui-components/components/dropdown-menu/dropdown-menu-type';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { useControlledComponentState } from 'ui-components/hooks/use-controlled-component-state/use-controlled-component-state';
import { ButtonThemeService } from 'ui-components/services/button-theme-service';

import { useSellerFormattedDate } from '../../hooks/use-datetime-formats';
import { FormDateComparison } from '../../services/filtering/filter-form-render-service/form-types/form-date-comparison';
import { shouldShowTwoDatePickers } from '../../services/filtering/filter-form-render-service/form-types/form-date-helper';

import messages from './messages';
import DateRangeOption from './prop-types/date-range-option';
import DateRangeOptions from './date-range-options';
import useDateRangeDate from './use-date-range-date';
import Global from '../../store/global-selectors';
import style from './style.module.less';

const elementIdsThatCloseDropdown = new Set(['date-range-remove', 'date-range-apply']);

export const DateRangeDropdown = ({
  id,
  dropdownMenuType,
  className = '',
  dateRangeOptions = DateRangeOptions,
  onDateRangeUpdated,
  startDate,
  endDate,
  dropdownTogglerTheme = 'white',
  dropdownTogglerClassname = '',
  buttonSize = 'normal',
  onApplyCallback,
  disabled = false,
  initialOption = null,
}) => {
  const managedProps = useMemo(() => ({ startDate, endDate }), [endDate, startDate]);
  const { localState, setLocalState, updateParent } = useControlledComponentState(onDateRangeUpdated, managedProps);

  const { actionHandlers, onRemoveClicked, onHandleApplyClicked, selectedOption } = useDateRangeDate({
    updateParent,
    localState,
    setLocalState,
    onApplyCallback,
    initialOption,
  });

  const userDateFormatString = useSelector(Global.selectUserUserDateFormat);

  const shouldAllowDropdownToClose = ({ target: { id: targetId } }) => {
    return targetId ? elementIdsThatCloseDropdown.has(targetId) : false;
  };

  const startDateStr = useSellerFormattedDate(startDate);
  const endDateStr = useSellerFormattedDate(endDate);

  const dropdownTogglerLabel = useMemo(() => {
    if (startDateStr && endDateStr) {
      return startDateStr === endDateStr ? startDateStr : `${startDateStr} - ${endDateStr}`;
    }
    return <FormattedMessage {...messages.selectDate} />;
  }, [startDateStr, endDateStr]);

  const isExpanded = useMemo(() => shouldShowTwoDatePickers(selectedOption), [selectedOption]);

  return (
    <DropdownMenu
      className={className}
      closeWhenChildrenClicked={false}
      containerAllowedMeToClose={shouldAllowDropdownToClose}
      disabled={disabled}
      dropdownMenuClassName={classnames(style['inner-dropdown'], {
        [style.expanded]: isExpanded,
      })}
      dropdownMenuType={dropdownMenuType}
      fullHeight
      id={id}
    >
      <DropdownMenuToggler buttonSize={buttonSize} className={dropdownTogglerClassname} theme={dropdownTogglerTheme}>
        {dropdownTogglerLabel}
      </DropdownMenuToggler>
      <FormDateComparison
        criterionOperatorOptions={dateRangeOptions}
        criterionStateMethods={actionHandlers}
        dateFormatString={userDateFormatString}
        dropdownMenuType={dropdownMenuType}
        formCriteriaClassname={style['date-range-options']}
      />
      <div className={style.footer}>
        <Button id='date-range-remove' onClick={onRemoveClicked} theme='link'>
          <FormattedMessage {...messages.remove} />
        </Button>
        <Button id='date-range-apply' onClick={onHandleApplyClicked} theme='primary'>
          <FormattedMessage {...messages.apply} />
        </Button>
      </div>
    </DropdownMenu>
  );
};

DateRangeDropdown.propTypes = {
  id: PropTypes.string,
  dropdownMenuType: DropdownMenuTypePropType,
  className: PropTypes.string,
  dateRangeOptions: PropTypes.arrayOf(DateRangeOption),
  onDateRangeUpdated: PropTypes.func.isRequired,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  dropdownTogglerTheme: PropTypes.oneOf(ButtonThemeService.getButtonThemes()),
  dropdownTogglerClassname: PropTypes.string,
  buttonSize: PropTypes.string,
  onApplyCallback: PropTypes.func,
  initialOption: PropTypes.string,
};
